import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useMemo, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {
  InstanceComplianceStandardRequest,
  InstanceComplianceStandardResponse,
} from 'src/api/ProactApiClient';
import {ModuleEnum, RiskAssessmentCodeAreaEnum} from 'src/api/ProactApiClient';
import {
  useCreateInstanceComplianceStandardMutation,
  useGetInstanceComplianceStandardQuery,
  useUpdateInstanceComplianceStandardMutation,
} from 'src/redux/queries/complianceStandard';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormViewRichTextEditorV2} from 'src/app/forms/FormViewRichTextEditor/FormViewRichTextEditorV2';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ViewField} from 'src/design/atoms/ViewField';
import {SidebarLinkComplianceRegulatory} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useGetDepartmentSelectOptionsQuery} from 'src/redux/queries/department';
import {useGetRiskAssessmentCodeByAreaQuery} from 'src/redux/queries/riskAsessmentCode';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {
  InstanceComplianceStandardFormMeta,
  InstanceComplianceStandardPillConfig,
} from './instanceComplianceStandardsHelper';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Compliance Standard',
  entityNamePlural: 'Compliance Standards',
  listPath: PathUtility.InstanceComplianceStandards('regulatory'),
  viewPath: id => PathUtility.InstanceComplianceStandardsView(id),
};

export interface InstanceComplianceStandardsFormProps {
  id?: number;
  mode: 'create' | 'view' | 'edit';
}

// The "Standard Information" fields are always rendered as read-only display
// values (see the Standard Information SectionCard below) — they never go
// through the editable config form, even in edit mode.
const STANDARD_INFO_FIELD_NAMES = new Set([
  'titleNumberLabel',
  'issuedAt',
  'content',
  'divider',
]);
const CONFIG_SECTION_FIELDS = [
  'active',
  'departmentIds',
  'summary',
  'note',
  'files',
];
const RISK_EDIT_FIELDS = [
  'riskAssessmentProbability',
  'riskAssessmentSeverity',
];

export const InstanceComplianceStandardsForm: React.FC<
  InstanceComplianceStandardsFormProps
> = ({id, mode}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);

  const [item, setItem] = useState<
    InstanceComplianceStandardResponse | undefined
  >(undefined);
  const [formData, setFormData] = useState<IFormDataV2>({});
  const [configFormMeta, setConfigFormMeta] = useState<IFormFieldV2[]>([]);

  const {data: riskAsset, isLoading: racIsLoading} =
    useGetRiskAssessmentCodeByAreaQuery(RiskAssessmentCodeAreaEnum.Safety);
  const {
    data: departments,
    isFetching: isDepartmentFetching,
    isLoading: isDepartmentLoading,
  } = useGetDepartmentSelectOptionsQuery({start: 0, length: 10000});

  const {
    data: fetchedItem,
    isLoading: isItemLoading,
    error: fetchError,
  } = useGetInstanceComplianceStandardQuery(id!, {
    skip: id === undefined,
  });

  const loadError = fetchError ? parseErrorMessage(fetchError) : undefined;

  const isLoading = isItemLoading;

  const isRefDataLoading =
    isDepartmentFetching || isDepartmentLoading || racIsLoading;

  // Build the editable config form meta (active, departments, summary, note,
  // files, risk fields) from the helper meta, filtering out the read-only
  // standard info fields that are rendered outside the form.
  const allEditMeta = useMemo<IFormFieldV2[]>(() => {
    const editableConfigMeta = configFormMeta.filter(
      f => !STANDARD_INFO_FIELD_NAMES.has(f.name)
    );

    // Risk score field is always read-only
    const riskScoreField = configFormMeta.find(f => f.name === 'riskScore');
    const riskScoreDisplayMeta: IFormFieldV2[] = riskScoreField
      ? [{...riskScoreField, readOnly: true}]
      : [];

    const configMeta = editableConfigMeta.filter(f =>
      CONFIG_SECTION_FIELDS.includes(f.name)
    );
    const riskEditMeta = editableConfigMeta.filter(f =>
      RISK_EDIT_FIELDS.includes(f.name)
    );

    return [...configMeta, ...riskEditMeta, ...riskScoreDisplayMeta];
  }, [configFormMeta]);

  const mergeStandardResponse = (
    entity: InstanceComplianceStandardResponse
  ): IFormDataV2 => toFormDataV2({...entity?.standard, ...entity});

  const form = useEntitySyncedForm({
    formMeta: allEditMeta,
    entity: fetchedItem,
    onEntityArrival: entity => {
      setItem(entity);
      setFormData(mergeStandardResponse(entity));
    },
    toFormData: mergeStandardResponse,
  });

  const liveActive = useWatch({control: form.methods.control, name: 'active'});

  useEffect(() => {
    if (!departments || !riskAsset) {
      return;
    }

    const departmentOptions = departments.data ?? [];
    const meta = InstanceComplianceStandardFormMeta(
      departmentOptions as ISelectOption[],
      riskAsset
    );

    setConfigFormMeta(meta);
  }, [departments, riskAsset]);

  const [createInstanceComplianceStandard] =
    useCreateInstanceComplianceStandardMutation();
  const [updateInstanceComplianceStandard] =
    useUpdateInstanceComplianceStandardMutation();

  const handleSave = async () => {
    try {
      setIsSavePending(true);

      const values = form.methods.getValues();
      const body: InstanceComplianceStandardRequest = {
        id: item?.id,
        complianceStandardId: item!.complianceStandardId,
        active: values.active as boolean,
        summary: values.summary as string | undefined,
        note: values.note as string | undefined,
        files: values.files as string | undefined,
        riskAssessmentProbability:
          (values.riskAssessmentProbability as number | undefined) || undefined,
        riskAssessmentSeverity:
          (values.riskAssessmentSeverity as number | undefined) || undefined,
        targetDepartmentIds: (
          values.departmentIds as ISelectOption[] | undefined
        )?.map(d => d.id as number),
      };

      const res =
        id === undefined
          ? await createInstanceComplianceStandard(body).unwrap()
          : await updateInstanceComplianceStandard(body).unwrap();

      setAlert(
        `${
          item?.standard?.sectionLabel ?? 'Standard'
        } was successfully updated`,
        'success'
      );

      setIsSavePending(false);
      navigate(PathUtility.InstanceComplianceStandardsView(String(res.id)), {
        replace: true,
      });
    } catch (error: unknown) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
    }
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const perms = useModulePermissions(ModuleEnum.Compliance);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const handleEdit = () =>
    navigate(PathUtility.InstanceComplianceStandardsEdit(id!));

  const pills = InstanceComplianceStandardPillConfig(
    isViewMode
      ? (formData?.active as boolean | undefined)
      : (liveActive as boolean | undefined)
  );

  // Navigation
  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: formData?.sectionLabel as string,
      }),
    [mode, id, formData?.sectionLabel]
  );

  const {goBack} = useBackNavigation(navigation.fallbackTo);

  const getTitle = () => {
    if (isCreateMode) return 'Create Compliance Standard';
    return (formData?.sectionLabel as string) ?? 'Item not found';
  };

  const saveButtons = (
    <>
      <PageHeader.Action variant="cancel" onClick={goBack}>
        Cancel
      </PageHeader.Action>
      <PageHeader.Action
        variant={isCreateMode ? 'create' : 'save'}
        onClick={handleValidateAndSave}
        loading={isSavePending}>
        {isCreateMode ? 'Create' : 'Save Changes'}
      </PageHeader.Action>
    </>
  );

  const backBar = (
    <BackBar
      fallbackTo={navigation.fallbackTo}
      fallbackLabel={navigation.fallbackLabel}
      breadcrumbs={navigation.breadcrumbs}
    />
  );

  const showLoader = isLoading || isRefDataLoading;

  if (showLoader) {
    return (
      <Stack gap="md" w="100%">
        {backBar}
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Stack gap="md" w="100%">
        {backBar}
        <Alert color="red" title="Error">
          {loadError}
        </Alert>
      </Stack>
    );
  }

  const resolveSelectLabel = (fieldName: string, value: unknown): string => {
    if (value == null) return '—';
    const field = configFormMeta.find(f => f.name === fieldName);
    const option = field?.options?.find(
      (o: {id?: unknown; label?: string}) => o.id === value
    );
    return option?.label ?? String(value);
  };

  const formatDate = (value: unknown): string => {
    if (!value) return '—';
    try {
      return new Date(value as string).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return String(value);
    }
  };

  return (
    <Stack gap="md" w="100%">
      {backBar}

      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkComplianceRegulatory.icon}
          subtitle={formData?.internalIdentifier as string | undefined}>
          {getTitle()}
        </PageHeader.Title>

        <PageHeader.Badges>
          {pills.map((pill, index) => (
            <PageHeader.Badge key={index} color={pill.color} variant="light">
              {pill.text}
            </PageHeader.Badge>
          ))}
          {isEditMode && (
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          )}
        </PageHeader.Badges>

        <PageHeader.Actions>
          {isViewMode && perms.update && (
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          )}
          {!isViewMode && saveButtons}
        </PageHeader.Actions>
      </PageHeader>

      {isViewMode ? (
        <Stack gap="md">
          <SectionCard
            title="Standard Information"
            data-testid="section-standard-information">
            <Box p="md">
              <Grid gutter="md">
                {formData?.titleNumberLabel && (
                  <Grid.Col span={12}>
                    <ViewField
                      label="Title"
                      value={formData.titleNumberLabel as string}
                    />
                  </Grid.Col>
                )}
                {formData?.issuedAt && (
                  <Grid.Col span={{base: 12, md: 6}}>
                    <ViewField
                      label="Last Issued Date"
                      value={formatDate(formData.issuedAt)}
                    />
                  </Grid.Col>
                )}
                {formData?.content && (
                  <Grid.Col span={12}>
                    <FormViewRichTextEditorV2
                      field={{name: 'content', label: '', type: 'richtext'}}
                      value={formData.content as string}
                    />
                  </Grid.Col>
                )}
              </Grid>
            </Box>
          </SectionCard>

          <SectionCard
            title="Configuration"
            data-testid="section-configuration">
            <Box p="md">
              <Grid gutter="md">
                <Grid.Col span={{base: 12, md: 6}}>
                  <ViewField
                    label="Active"
                    value={formData?.active ? 'Yes' : 'No'}
                  />
                </Grid.Col>
                {formData?.summary && (
                  <Grid.Col span={12}>
                    <ViewField
                      label="Summary"
                      value={formData.summary as string}
                    />
                  </Grid.Col>
                )}
                {formData?.note && (
                  <Grid.Col span={12}>
                    <FormViewRichTextEditorV2
                      field={{name: 'note', label: 'Notes', type: 'richtext'}}
                      value={formData.note as string}
                    />
                  </Grid.Col>
                )}
              </Grid>
            </Box>
          </SectionCard>

          {(formData?.riskAssessmentProbability != null ||
            formData?.riskAssessmentSeverity != null ||
            formData?.riskScore != null) && (
            <SectionCard
              title="Risk Assessment"
              data-testid="section-risk-assessment">
              <Box p="md">
                <Grid gutter="md">
                  {formData?.riskAssessmentProbability != null && (
                    <Grid.Col span={{base: 12, md: 4}}>
                      <ViewField
                        label="Risk Probability"
                        value={resolveSelectLabel(
                          'riskAssessmentProbability',
                          formData.riskAssessmentProbability
                        )}
                      />
                    </Grid.Col>
                  )}
                  {formData?.riskAssessmentSeverity != null && (
                    <Grid.Col span={{base: 12, md: 4}}>
                      <ViewField
                        label="Risk Severity"
                        value={resolveSelectLabel(
                          'riskAssessmentSeverity',
                          formData.riskAssessmentSeverity
                        )}
                      />
                    </Grid.Col>
                  )}
                  {formData?.riskScore != null && (
                    <Grid.Col span={{base: 12, md: 4}}>
                      <ViewField
                        label="Risk Score"
                        value={String(formData.riskScore)}
                      />
                    </Grid.Col>
                  )}
                </Grid>
              </Box>
            </SectionCard>
          )}
        </Stack>
      ) : (
        <ProactForm form={form}>
          <Stack gap="md">
            <SectionCard
              title="Standard Information"
              data-testid="section-standard-information">
              <Box p="md">
                <Grid gutter="md">
                  <Grid.Col span={12}>
                    <ViewField
                      label="Title"
                      value={
                        (formData?.titleNumberLabel as string) ||
                        (formData?.sectionLabel as string) ||
                        '—'
                      }
                    />
                  </Grid.Col>
                  {formData?.issuedAt && (
                    <Grid.Col span={{base: 12, md: 6}}>
                      <ViewField
                        label="Last Issued Date"
                        value={formatDate(formData.issuedAt)}
                      />
                    </Grid.Col>
                  )}
                  {formData?.content && (
                    <Grid.Col span={12}>
                      <FormViewRichTextEditorV2
                        field={{name: 'content', label: '', type: 'richtext'}}
                        value={formData.content as string}
                      />
                    </Grid.Col>
                  )}
                </Grid>
              </Box>
            </SectionCard>

            <SectionCard
              title="Configuration"
              data-testid="section-configuration">
              <Box p="md">
                <Grid gutter="md">
                  <ProactField name="active" />
                  <ProactField name="departmentIds" />
                  <ProactField name="summary" />
                  <ProactField name="note" />
                  <ProactField name="files" />
                </Grid>
              </Box>
            </SectionCard>

            <SectionCard
              title="Risk Assessment"
              data-testid="section-risk-assessment">
              <Box p="md">
                <Grid gutter="md">
                  <ProactField name="riskAssessmentProbability" />
                  <ProactField name="riskAssessmentSeverity" />
                  <ProactField name="riskScore" />
                </Grid>
              </Box>
            </SectionCard>

            <Group mt="md">{saveButtons}</Group>
          </Stack>
        </ProactForm>
      )}
    </Stack>
  );
};
